import type { ReactNode } from "react";
import {
  ArrowDown,
  ArrowRight,
  Download,
  FileArchive,
  Paperclip,
  Send,
  Server,
} from "lucide-react";
import { MockFrame } from "@/components/landing/primitives";
import { DotChip } from "@/components/shared/DotChip";
import { makeTr, type LandingLocale } from "@/lib/landing/locale";
import { cn } from "@/lib/utils";

/**
 * Pictures for the guide pages, drawn from the app's own pieces (prerender-
 * safe redraws, like the landing mockups). Names and amounts are invented;
 * what the pictures show exists in the app:
 * - accountants: the Chybějící doklady page (src/pages/MissingDocuments.tsx);
 * - pohoda: the "Export pro Pohodu" card (PohodaExportCard) and its send result;
 * - the POHODA flow: export ZIP or mServer send, then POHODA's settlement
 *   by the pairing symbol (docs/exports/POHODA.md).
 */

export type GuideVisualKey = "accountants" | "pohoda";
export type GuideDiagramKey = "pohodaFlow";

const amber = "hsl(var(--dot-amber))";

function MissingDocumentsMock({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  const rows = [
    { date: "4. 10.", who: "Tiskárna Pixel", amount: "−2 890,00 Kč" },
    { date: "11. 10.", who: "Parkování Centrum", amount: "−120,00 Kč" },
    { date: "26. 10.", who: tr("Nájem kanceláře", "Office rent"), amount: "−18 500,00 Kč" },
  ];
  return (
    <MockFrame
      label={tr(
        "Ukázka stránky Chybějící doklady: odchozí platby bez dokladu s tlačítky Přiložit doklad a Nepotřebuje doklad. Vymyšlená data.",
        "Sample Missing documents page: outgoing payments without a document, with Attach document and Needs no document. Invented data."
      )}
      className="bg-card"
    >
      <div className="flex items-center justify-between border-b px-4 py-3">
        <p className="text-[13px] font-semibold">{tr("Chybějící doklady", "Missing documents")}</p>
        <span className="text-xs text-muted-foreground">{tr("90 dní", "90 days")}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 border-b p-4">
        <div className="rounded-lg border bg-background/50 px-3 py-2">
          <p className="text-xs text-muted-foreground">{tr("Bez dokladu", "No document")}</p>
          <p className="text-xl font-semibold tabular-nums">3</p>
        </div>
        <div className="rounded-lg border bg-background/50 px-3 py-2">
          <p className="text-xs text-muted-foreground">{tr("Celkem", "Total")}</p>
          <p className="text-xl font-semibold tabular-nums">21 510 Kč</p>
        </div>
      </div>
      <ul className="divide-y text-[13px]">
        {rows.map((r, i) => (
          <li
            key={r.date}
            className="grid grid-cols-[3.25rem_minmax(0,1fr)_auto] items-center gap-x-3 px-4 py-2.5"
          >
            <span className="tabular-nums text-muted-foreground">{r.date}</span>
            <span className="min-w-0">
              <span className="block truncate">{r.who}</span>
              {i === 0 && (
                <span className="mt-1 inline-flex items-center gap-1 rounded-md bg-primary/15 px-1.5 py-0.5 text-[11px] font-medium text-[hsl(var(--action))] dark:text-primary">
                  <Paperclip className="size-3" />
                  {tr("Přiložit doklad", "Attach document")}
                </span>
              )}
            </span>
            <span className="whitespace-nowrap font-mono tabular-nums">{r.amount}</span>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-2 border-t px-4 py-2.5 text-xs text-muted-foreground">
        <span className="size-1.5 rounded-full" style={{ backgroundColor: amber }} />
        {tr(
          "V pondělí přijde e-mail s platbami bez dokladu.",
          "On Monday an e-mail lists the payments without a document."
        )}
      </div>
    </MockFrame>
  );
}

function PohodaExportMock({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  return (
    <MockFrame
      label={tr(
        "Ukázka karty Export pro Pohodu: měsíc, zkratka účtu, stažení ZIPu nebo odeslání do POHODY a výsledek. Vymyšlená data.",
        "Sample Export for Pohoda card: month, account abbreviation, ZIP download or send to POHODA, and the result. Invented data."
      )}
      className="bg-card"
    >
      <div className="border-b px-4 py-3">
        <p className="text-[13px] font-semibold">{tr("Export pro Pohodu", "Export for Pohoda")}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {tr("Přijaté doklady a pohyby na účtu", "Received documents and account movements")}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2 p-4 text-[13px]">
        <div className="rounded-lg border bg-background/50 px-3 py-2">
          <p className="text-xs text-muted-foreground">{tr("Měsíc", "Month")}</p>
          <p className="font-medium">{tr("říjen 2026", "October 2026")}</p>
        </div>
        <div className="rounded-lg border bg-background/50 px-3 py-2">
          <p className="text-xs text-muted-foreground">
            {tr("Účet v POHODĚ", "Account in POHODA")}
          </p>
          <p className="font-mono font-medium">FIO</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 px-4 pb-4">
        <span className="inline-flex items-center gap-1.5 rounded-md border bg-background/60 px-2.5 py-1.5 text-xs font-medium">
          <Download className="size-3.5 text-muted-foreground" />
          {tr("Stáhnout ZIP", "Download ZIP")}
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-md bg-[hsl(var(--action))] px-2.5 py-1.5 text-xs font-medium text-[hsl(var(--action-foreground))] dark:bg-primary dark:text-primary-foreground">
          <Send className="size-3.5" />
          {tr("Odeslat do POHODY", "Send to POHODA")}
        </span>
      </div>
      <div className="space-y-1.5 border-t px-4 py-3 text-[13px]">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{tr("Doklady", "Documents")}</span>
          <span className="flex gap-3">
            <DotChip color="green">{tr("38 naimportováno", "38 imported")}</DotChip>
            <DotChip color="blue">{tr("2 už v POHODĚ", "2 already there")}</DotChip>
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{tr("Pohyby na účtu", "Account movements")}</span>
          <DotChip color="green">{tr("57 naimportováno", "57 imported")}</DotChip>
        </div>
      </div>
    </MockFrame>
  );
}

/** The picture beside a guide's opening answer. */
export function GuideVisual({ visual, locale }: { visual: GuideVisualKey; locale: LandingLocale }) {
  return visual === "accountants" ? (
    <MissingDocumentsMock locale={locale} />
  ) : (
    <PohodaExportMock locale={locale} />
  );
}

function FlowBox({
  title,
  text,
  icon,
  accent,
}: {
  title: string;
  text: string;
  icon: ReactNode;
  accent?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex h-full gap-3 rounded-xl border bg-card p-4",
        accent && "border-primary/40 bg-primary/10"
      )}
    >
      <span
        aria-hidden="true"
        className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-[hsl(var(--action))] dark:text-primary"
      >
        {icon}
      </span>
      <div>
        <p className="text-sm font-semibold">{title}</p>
        <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">{text}</p>
      </div>
    </div>
  );
}

function Arrow() {
  return (
    <div aria-hidden="true" className="flex items-center justify-center text-muted-foreground">
      <ArrowDown className="size-5 lg:hidden" />
      <ArrowRight className="hidden size-5 lg:block" />
    </div>
  );
}

/** Zettl → (ZIP or mServer) → POHODA, as boxes and arrows. */
export function PohodaFlow({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  return (
    <figure>
      <figcaption className="sr-only">
        {tr(
          "Schéma: doklady a platby z Fio v Zettlu, odtud buď ZIP s XML pro import, nebo přímé odeslání přes mServer, a v POHODĚ automatická likvidace podle párového symbolu.",
          "Diagram: documents and Fio payments in Zettl, then either a ZIP of XML to import or a direct send through mServer, and in POHODA automatic settlement by the pairing symbol."
        )}
      </figcaption>
      <div
        aria-hidden="true"
        className="grid items-stretch gap-3 lg:grid-cols-[1fr_auto_1.15fr_auto_1fr] lg:gap-4"
      >
        <FlowBox
          icon={<FileArchive className="size-4" />}
          title={tr("Zettl", "Zettl")}
          text={tr(
            "Přijaté doklady a pohyby na účtu z Fio za měsíc.",
            "The month's received documents and Fio account movements."
          )}
        />
        <Arrow />
        <div className="grid gap-3">
          <FlowBox
            icon={<Download className="size-4" />}
            title={tr("ZIP s XML", "ZIP of XML")}
            text={tr("Stáhnete a naimportujete v POHODĚ.", "Download it and import it in POHODA.")}
          />
          <FlowBox
            icon={<Server className="size-4" />}
            title={tr("Přímo přes mServer", "Directly through mServer")}
            text={tr(
              "Když je mServer dostupný z internetu přes HTTPS.",
              "When mServer is reachable from the internet over HTTPS."
            )}
          />
        </div>
        <Arrow />
        <FlowBox
          accent
          icon={<Send className="size-4" />}
          title="POHODA"
          text={tr(
            "Automatická likvidace páruje platby s doklady podle párového symbolu.",
            "Automatic settlement pairs payments with documents by the pairing symbol."
          )}
        />
      </div>
    </figure>
  );
}

/** A diagram section's content, by key. */
export function GuideDiagram({
  diagram,
  locale,
}: {
  diagram: GuideDiagramKey;
  locale: LandingLocale;
}) {
  return diagram === "pohodaFlow" ? <PohodaFlow locale={locale} /> : null;
}
